import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, BarChart3, Ghost, Upload, Timer, Eye, MousePointerClick, XCircle, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Toggle({ on, onToggle, disabled, testId }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      data-testid={testId}
      className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none shrink-0 ${on ? 'bg-brand' : 'bg-neutral-700'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-7' : 'translate-x-0'}`} />
    </button>
  );
}

const DEFAULT_HALLOWEEN_GIFS = [
  { url: 'https://media.giphy.com/media/R3bZpfrwFm4nd89f1Z/giphy.gif', label: 'PIG' },
  { url: 'https://media.giphy.com/media/26Ff3DaJ1zWk97lV6/giphy.gif', label: 'CLOWN' },
  { url: 'https://media1.tenor.com/m/DE4g43Bdaj0AAAAC/leatherface-horror.gif', label: 'CHAINSAW' },
];

const INITIAL = {
  halloween_enabled: false,
  halloween_title: 'HAUNTED HOUSE',
  halloween_title_ja: 'ホーンテッド・ハウス',
  halloween_subtitle: "Limited Halloween Experience — Book Before It's Gone",
  halloween_subtitle_ja: '期間限定ハロウィン体験 — 満席になる前にご予約を',
  halloween_cta_label: 'BOOK THE FRIGHT',
  halloween_cta_label_ja: '恐怖を予約する',
  halloween_cta_sublabel: 'Admit One',
  halloween_cta_sublabel_ja: '1名様',
  halloween_cta_ticket_theme: 'neon-red',
  halloween_cta_url: '',
  halloween_ticket_weekday_url: '',
  halloween_ticket_weekend_url: '',
  halloween_dates: 'October 25 – November 2',
  halloween_dates_ja: '10月25日 – 11月2日',
  halloween_live_actors: 'LIVE ACTORS ON WEEKENDS',
  halloween_live_actors_ja: '週末は本物の俳優が出演',
  halloween_disclaimer: 'We are not responsible for guests who disappear.',
  halloween_disclaimer_ja: '行方不明になったお客様につきましては当店は一切責任を負いません。',
  halloween_target_date: '10-31',
  halloween_hide_after: '11-03',
  halloween_scare_seconds: 30,
  halloween_scare_duration_ms: 6000,
  halloween_scare_max_per_visitor: 3,
  halloween_disappeared_baseline: 0,
  halloween_gifs: DEFAULT_HALLOWEEN_GIFS,
};

export default function HalloweenBannerConfig() {
  const [halloween, setHalloween] = useState(INITIAL);
  const [gifUploading, setGifUploading] = useState([false, false, false]);
  const [halloweenAnalytics, setHalloweenAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [halloweenSaving, setHalloweenSaving] = useState(false);
  const [halloweenSaved, setHalloweenSaved] = useState(false);

  const loadHalloweenAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/halloween/analytics`, { withCredentials: true });
      setHalloweenAnalytics(data);
    } catch { /* ignore */ }
    setAnalyticsLoading(false);
  };

  useEffect(() => { loadHalloweenAnalytics(); }, []);

  useEffect(() => {
    axios.get(`${API}/api/settings/site`, { withCredentials: true }).then(({ data }) => {
      setHalloween({
        halloween_enabled: !!data.halloween_enabled,
        halloween_title: data.halloween_title || 'HAUNTED HOUSE',
        halloween_title_ja: data.halloween_title_ja || '',
        halloween_subtitle: data.halloween_subtitle || "Limited Halloween Experience — Book Before It's Gone",
        halloween_subtitle_ja: data.halloween_subtitle_ja || '',
        halloween_cta_label: data.halloween_cta_label || 'BOOK THE FRIGHT',
        halloween_cta_label_ja: data.halloween_cta_label_ja || '',
        halloween_cta_sublabel: data.halloween_cta_sublabel || 'Admit One',
        halloween_cta_sublabel_ja: data.halloween_cta_sublabel_ja || '',
        halloween_cta_ticket_theme: data.halloween_cta_ticket_theme || 'neon-red',
        halloween_cta_url: data.halloween_cta_url || '',
        halloween_ticket_weekday_url: data.halloween_ticket_weekday_url || '',
        halloween_ticket_weekend_url: data.halloween_ticket_weekend_url || '',
        halloween_dates: data.halloween_dates || 'October 25 – November 2',
        halloween_dates_ja: data.halloween_dates_ja || '',
        halloween_live_actors: data.halloween_live_actors || 'LIVE ACTORS ON WEEKENDS',
        halloween_live_actors_ja: data.halloween_live_actors_ja || '',
        halloween_disclaimer: data.halloween_disclaimer || 'We are not responsible for guests who disappear.',
        halloween_disclaimer_ja: data.halloween_disclaimer_ja || '',
        halloween_target_date: data.halloween_target_date || '10-31',
        halloween_hide_after: data.halloween_hide_after || '11-03',
        halloween_scare_seconds: data.halloween_scare_seconds || 30,
        halloween_scare_duration_ms: data.halloween_scare_duration_ms || 6000,
        halloween_scare_max_per_visitor: data.halloween_scare_max_per_visitor || 3,
        halloween_disappeared_baseline: data.halloween_disappeared_baseline || 0,
        halloween_gifs: Array.isArray(data.halloween_gifs) && data.halloween_gifs.length === 3
          ? data.halloween_gifs
          : DEFAULT_HALLOWEEN_GIFS,
      });
    }).catch(() => {});
  }, []);

  return (
    <div className="border-2 border-orange-600/40 bg-gradient-to-br from-orange-950/30 to-black rounded-xl p-5" data-testid="halloween-section">
      <div className="flex items-center gap-2 mb-4">
        <Ghost size={18} className="text-orange-500" />
        <h3 className="font-heading text-sm tracking-widest uppercase text-orange-500">Halloween Haunted House Banner</h3>
      </div>
      <p className="font-sans text-xs text-neutral-500 mb-4">
        Bright neon-orange seasonal banner shown above the hero on the landing page. Toggle it off after Halloween.
      </p>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <p className="font-heading text-base tracking-wide uppercase text-white">Show banner</p>
          <p className="font-sans text-xs text-neutral-500 mt-1">
            {halloween.halloween_enabled ? 'Live on the homepage right now' : 'Hidden — homepage looks normal'}
          </p>
        </div>
        <Toggle
          on={halloween.halloween_enabled}
          onToggle={async () => {
            const next = !halloween.halloween_enabled;
            setHalloweenSaving(true);
            try {
              const { data } = await axios.put(`${API}/api/settings/site`, { halloween_enabled: next }, { withCredentials: true });
              setHalloween(h => ({ ...h, halloween_enabled: !!data.halloween_enabled }));
            } catch {}
            setHalloweenSaving(false);
          }}
          disabled={halloweenSaving}
          testId="halloween-toggle-btn"
        />
      </div>
      <div className="space-y-3">
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Banner Title <span className="normal-case text-neutral-600">(EN)</span></label>
          <input value={halloween.halloween_title} onChange={e => setHalloween(h => ({ ...h, halloween_title: e.target.value }))} placeholder="HAUNTED HOUSE" data-testid="halloween-title-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-heading text-sm tracking-wider text-orange-300 focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">バナー タイトル <span className="normal-case text-neutral-600">(JA — shown when 日本語 toggle is on)</span></label>
          <input value={halloween.halloween_title_ja} onChange={e => setHalloween(h => ({ ...h, halloween_title_ja: e.target.value }))} placeholder="ホーンテッド・ハウス" data-testid="halloween-title-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-heading text-sm text-orange-300 focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Subtitle <span className="normal-case text-neutral-600">(EN)</span></label>
          <input value={halloween.halloween_subtitle} onChange={e => setHalloween(h => ({ ...h, halloween_subtitle: e.target.value }))} data-testid="halloween-subtitle-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">サブタイトル <span className="normal-case text-neutral-600">(JA)</span></label>
          <input value={halloween.halloween_subtitle_ja} onChange={e => setHalloween(h => ({ ...h, halloween_subtitle_ja: e.target.value }))} data-testid="halloween-subtitle-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Dates <span className="normal-case text-neutral-600">(EN)</span></label>
            <input value={halloween.halloween_dates} onChange={e => setHalloween(h => ({ ...h, halloween_dates: e.target.value }))} placeholder="October 25 – November 2" data-testid="halloween-dates-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">開催期間 <span className="normal-case text-neutral-600">(JA)</span></label>
            <input value={halloween.halloween_dates_ja} onChange={e => setHalloween(h => ({ ...h, halloween_dates_ja: e.target.value }))} placeholder="10月25日 – 11月2日" data-testid="halloween-dates-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Button Label <span className="normal-case text-neutral-600">(EN)</span></label>
            <input value={halloween.halloween_cta_label} onChange={e => setHalloween(h => ({ ...h, halloween_cta_label: e.target.value }))} placeholder="BOOK THE FRIGHT" data-testid="halloween-cta-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">ボタンラベル <span className="normal-case text-neutral-600">(JA)</span></label>
            <input value={halloween.halloween_cta_label_ja} onChange={e => setHalloween(h => ({ ...h, halloween_cta_label_ja: e.target.value }))} placeholder="恐怖を予約する" data-testid="halloween-cta-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Ticket Sublabel <span className="normal-case text-neutral-600">(EN — small text under button)</span></label>
            <input value={halloween.halloween_cta_sublabel} onChange={e => setHalloween(h => ({ ...h, halloween_cta_sublabel: e.target.value }))} placeholder="Admit One" data-testid="halloween-cta-sublabel-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">チケット副題 <span className="normal-case text-neutral-600">(JA)</span></label>
            <input value={halloween.halloween_cta_sublabel_ja} onChange={e => setHalloween(h => ({ ...h, halloween_cta_sublabel_ja: e.target.value }))} placeholder="1名様" data-testid="halloween-cta-sublabel-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-orange-500 focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Ticket Color Theme <span className="normal-case text-neutral-600">(carnival ticket palette)</span></label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="halloween-ticket-theme-picker">
            {[
              { key: 'neon-red', label: 'Neon Red', swatch: 'linear-gradient(135deg, #150000 0%, #ff2a2a 50%, #0a0000 100%)' },
              { key: 'halloween', label: 'Halloween', swatch: 'linear-gradient(135deg, #3d0a5c 0%, #ff6a00 50%, #ffd23f 100%)' },
              { key: 'classic', label: 'Classic', swatch: 'linear-gradient(135deg, #1c2b6b 0%, #fff5e0 50%, #d43a2a 100%)' },
              { key: 'blood', label: 'Blood', swatch: 'linear-gradient(135deg, #0a0000 0%, #4a0000 50%, #7a0000 100%)' },
              { key: 'gold', label: 'Gold', swatch: 'linear-gradient(135deg, #0a0a0a 0%, #ffcf3d 50%, #8a0000 100%)' },
              { key: 'orange-black', label: 'Orange & Black', swatch: 'linear-gradient(135deg, #0a0a0a 0%, #ff7a1a 50%, #0a0a0a 100%)' },
              { key: 'white-red', label: 'White & Red', swatch: 'linear-gradient(135deg, #ffffff 0%, #ff1a1a 50%, #ffffff 100%)' },
              { key: 'ghost', label: 'Ghost White', swatch: 'linear-gradient(135deg, #f0f0f0 0%, #ffffff 50%, #b0b0b0 100%)' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setHalloween(h => ({ ...h, halloween_cta_ticket_theme: opt.key }))}
                data-testid={`halloween-ticket-theme-${opt.key}`}
                className={`relative rounded-lg overflow-hidden border-2 px-2 py-2 transition-all ${halloween.halloween_cta_ticket_theme === opt.key ? 'border-orange-400 ring-2 ring-orange-500/50 scale-[1.02]' : 'border-neutral-700 hover:border-neutral-500'}`}
              >
                <div className="h-8 w-full rounded-md" style={{ background: opt.swatch }} />
                <div className="mt-1.5 font-heading text-[10px] tracking-widest uppercase text-white">{opt.label}</div>
                {halloween.halloween_cta_ticket_theme === opt.key && (
                  <div className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-orange-500 flex items-center justify-center text-[9px] text-black font-bold">✓</div>
                )}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-neutral-600 mt-1.5">Changes the carnival ticket colors on the public banner.</p>
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Live Actors Badge <span className="normal-case text-neutral-600">(EN — next to dates)</span></label>
          <input value={halloween.halloween_live_actors} onChange={e => setHalloween(h => ({ ...h, halloween_live_actors: e.target.value }))} placeholder="LIVE ACTORS ON WEEKENDS" data-testid="halloween-live-actors-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-heading text-sm tracking-widest uppercase text-red-200 focus:border-orange-500 focus:outline-none" />
          <p className="text-[10px] text-neutral-600 mt-1">Leave empty to hide the badge</p>
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">俳優バッジ <span className="normal-case text-neutral-600">(JA)</span></label>
          <input value={halloween.halloween_live_actors_ja} onChange={e => setHalloween(h => ({ ...h, halloween_live_actors_ja: e.target.value }))} placeholder="週末は本物の俳優が出演" data-testid="halloween-live-actors-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-heading text-sm text-red-200 focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Disclaimer Catchphrase <span className="normal-case text-neutral-600">(EN)</span></label>
          <input value={halloween.halloween_disclaimer} onChange={e => setHalloween(h => ({ ...h, halloween_disclaimer: e.target.value }))} placeholder="We are not responsible for guests who disappear." data-testid="halloween-disclaimer-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans italic text-sm text-orange-200 focus:border-orange-500 focus:outline-none" />
          <p className="text-[10px] text-neutral-600 mt-1">Small italic line shown below the dates</p>
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">免責事項キャッチフレーズ <span className="normal-case text-neutral-600">(JA)</span></label>
          <input value={halloween.halloween_disclaimer_ja} onChange={e => setHalloween(h => ({ ...h, halloween_disclaimer_ja: e.target.value }))} placeholder="行方不明になったお客様につきましては当店は一切責任を負いません。" data-testid="halloween-disclaimer-ja-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans italic text-sm text-orange-200 focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Guests Disappeared — Starting Number</label>
          <input type="number" min={0} max={999999} value={halloween.halloween_disappeared_baseline} onChange={e => setHalloween(h => ({ ...h, halloween_disappeared_baseline: Math.max(0, Math.min(999999, parseInt(e.target.value, 10) || 0)) }))} placeholder="0" data-testid="halloween-disappeared-baseline-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm text-red-300 focus:border-red-500 focus:outline-none" />
          <p className="text-[10px] text-neutral-600 mt-1">Real jump-scare fires get added on top — set a spooky base (e.g. 666, 1313)</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Countdown Target <span className="normal-case text-neutral-600">(MM-DD)</span></label>
            <input value={halloween.halloween_target_date} onChange={e => setHalloween(h => ({ ...h, halloween_target_date: e.target.value }))} placeholder="10-31" data-testid="halloween-target-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm text-white focus:border-orange-500 focus:outline-none" />
            <p className="text-[10px] text-neutral-600 mt-1">Banner shows "X days until Halloween"</p>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Auto-Hide After <span className="normal-case text-neutral-600">(MM-DD)</span></label>
            <input value={halloween.halloween_hide_after} onChange={e => setHalloween(h => ({ ...h, halloween_hide_after: e.target.value }))} placeholder="11-03" data-testid="halloween-hide-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm text-white focus:border-orange-500 focus:outline-none" />
            <p className="text-[10px] text-neutral-600 mt-1">Banner disappears entirely after this date</p>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 flex items-center gap-1"><Timer size={11} /> Scare Cooldown <span className="normal-case text-neutral-600">(sec)</span></label>
            <input type="number" min={10} max={300} value={halloween.halloween_scare_seconds} onChange={e => setHalloween(h => ({ ...h, halloween_scare_seconds: Math.max(10, Math.min(300, parseInt(e.target.value, 10) || 30)) }))} placeholder="30" data-testid="halloween-scare-secs-input" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm text-white focus:border-orange-500 focus:outline-none" />
            <p className="text-[10px] text-neutral-600 mt-1">Gap between jump scares (10–300)</p>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1"><Ghost size={11} /> Scare Duration</span>
              <span className="font-mono text-orange-300 normal-case">{(halloween.halloween_scare_duration_ms / 1000).toFixed(1)}s</span>
            </label>
            <input type="range" min={3000} max={10000} step={500} value={halloween.halloween_scare_duration_ms} onChange={e => setHalloween(h => ({ ...h, halloween_scare_duration_ms: parseInt(e.target.value, 10) }))} data-testid="halloween-scare-duration-slider" className="halloween-scare-slider w-full mt-2" />
            <div className="flex justify-between text-[10px] text-neutral-600 mt-1 font-mono">
              <span>3s</span>
              <span>10s</span>
            </div>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 flex items-center gap-1">
              <Ghost size={11} /> Max Scares Per Visitor
            </label>
            <input
              type="number" min={1} max={20}
              value={halloween.halloween_scare_max_per_visitor}
              onChange={e => setHalloween(h => ({
                ...h,
                halloween_scare_max_per_visitor: Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 3))
              }))}
              placeholder="3"
              data-testid="halloween-scare-max-input"
              className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm text-white focus:border-orange-500 focus:outline-none"
            />
            <p className="text-[10px] text-neutral-600 mt-1">Stop scaring the same guest after this many hits today (resets nightly).</p>
          </div>
        </div>
        <div className="border-t border-orange-900/40 pt-4 mt-1" data-testid="halloween-gifs-editor">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-heading text-[11px] tracking-widest uppercase text-orange-300">Scary GIFs (3-up under banner)</p>
              <p className="font-sans text-[11px] text-neutral-500 mt-0.5">Only shows when the banner is ON. Paste any Giphy/Tenor direct <span className="font-mono">.gif</span> URL.</p>
            </div>
            <button
              type="button"
              data-testid="halloween-gifs-reset"
              onClick={() => setHalloween(h => ({ ...h, halloween_gifs: DEFAULT_HALLOWEEN_GIFS }))}
              className="font-heading text-[10px] tracking-widest uppercase text-orange-400/80 hover:text-orange-300 underline underline-offset-4"
            >
              Reset to defaults
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {(halloween.halloween_gifs || DEFAULT_HALLOWEEN_GIFS).map((g, i) => (
              <div key={i} className="border border-red-900/60 rounded-lg p-2 bg-black/60" data-testid={`halloween-gif-editor-${i}`}>
                <div className="aspect-[3/4] w-full mb-2 rounded-md overflow-hidden bg-black border border-red-800/40">
                  {g.url ? (
                    <img src={g.url} alt={g.label || `gif ${i+1}`} className="w-full h-full object-cover" style={{ filter: 'contrast(1.15) saturate(0.85)' }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-600 text-[10px] uppercase tracking-widest">No preview</div>
                  )}
                </div>
                <label className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mb-1 block">Label</label>
                <input
                  value={g.label || ''}
                  onChange={e => {
                    const next = [...(halloween.halloween_gifs || DEFAULT_HALLOWEEN_GIFS)];
                    next[i] = { ...next[i], label: e.target.value };
                    setHalloween(h => ({ ...h, halloween_gifs: next }));
                  }}
                  data-testid={`halloween-gif-label-${i}`}
                  placeholder={DEFAULT_HALLOWEEN_GIFS[i].label}
                  className="w-full bg-black border border-neutral-700 rounded-md px-2 py-1.5 font-heading text-xs tracking-widest text-red-300 focus:border-red-500 focus:outline-none mb-2"
                />
                <label className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mb-1 block">GIF URL</label>
                <input
                  value={g.url || ''}
                  onChange={e => {
                    const next = [...(halloween.halloween_gifs || DEFAULT_HALLOWEEN_GIFS)];
                    next[i] = { ...next[i], url: e.target.value };
                    setHalloween(h => ({ ...h, halloween_gifs: next }));
                  }}
                  data-testid={`halloween-gif-url-${i}`}
                  placeholder="https://media.giphy.com/media/..."
                  className="w-full bg-black border border-neutral-700 rounded-md px-2 py-1.5 font-mono text-[11px] text-white focus:border-red-500 focus:outline-none"
                />
                <label className="mt-2 flex items-center justify-center gap-1.5 font-heading text-[10px] tracking-widest uppercase bg-red-950/60 border border-red-800/60 text-red-300 hover:bg-red-900/60 hover:text-red-200 rounded-md px-2 py-2 cursor-pointer transition-colors">
                  {gifUploading[i] ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {gifUploading[i] ? 'Uploading...' : 'Upload GIF'}
                  <input
                    type="file"
                    accept="image/gif,image/png,image/jpeg,image/webp"
                    disabled={gifUploading[i]}
                    data-testid={`halloween-gif-upload-${i}`}
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (f.size > 10 * 1024 * 1024) { window.alert('File too large (max 10MB)'); return; }
                      setGifUploading(prev => { const n = [...prev]; n[i] = true; return n; });
                      try {
                        const fd = new FormData();
                        fd.append('file', f);
                        const { data } = await axios.post(`${API}/api/admin/halloween/upload-gif`, fd, {
                          withCredentials: true,
                          headers: { 'Content-Type': 'multipart/form-data' },
                        });
                        const next = [...(halloween.halloween_gifs || DEFAULT_HALLOWEEN_GIFS)];
                        next[i] = { ...next[i], url: data.url };
                        setHalloween(h => ({ ...h, halloween_gifs: next }));
                      } catch (err) {
                        window.alert(err.response?.data?.detail || 'Upload failed');
                      }
                      setGifUploading(prev => { const n = [...prev]; n[i] = false; return n; });
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={async () => {
              setHalloweenSaving(true);
              try {
                const { data } = await axios.put(`${API}/api/settings/site`, halloween, { withCredentials: true });
                setHalloween(prev => ({ ...prev, ...data, halloween_enabled: !!data.halloween_enabled,
                  halloween_gifs: Array.isArray(data.halloween_gifs) && data.halloween_gifs.length === 3 ? data.halloween_gifs : DEFAULT_HALLOWEEN_GIFS,
                }));
                setHalloweenSaved(true);
                setTimeout(() => setHalloweenSaved(false), 2500);
              } catch (err) {
                window.alert(err.response?.data?.detail || 'Save failed');
              }
              setHalloweenSaving(false);
            }}
            disabled={halloweenSaving}
            data-testid="halloween-save-btn"
            className="font-heading text-xs tracking-widest uppercase bg-orange-500 text-black px-5 py-2.5 rounded-lg hover:bg-orange-400 transition-colors disabled:opacity-50"
          >
            {halloweenSaving ? 'Saving...' : 'Save Halloween Copy'}
          </button>
          {halloweenSaved && <span className="font-sans text-xs text-green-400">Saved!</span>}
        </div>
        <div className="border-t border-orange-900/40 pt-4 mt-2" data-testid="halloween-analytics">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 size={14} className="text-orange-400" />
              <p className="font-heading text-[11px] tracking-widest uppercase text-orange-300">Halloween Analytics</p>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={loadHalloweenAnalytics} data-testid="halloween-analytics-refresh"
                className="font-heading text-[10px] tracking-widest uppercase text-orange-400/80 hover:text-orange-300 underline underline-offset-4">
                {analyticsLoading ? 'Loading...' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Reset all Halloween analytics? This clears every view, click and dismiss.')) return;
                  try {
                    await axios.delete(`${API}/api/admin/halloween/analytics`, { withCredentials: true });
                    await loadHalloweenAnalytics();
                  } catch { window.alert('Reset failed'); }
                }}
                data-testid="halloween-analytics-reset"
                className="font-heading text-[10px] tracking-widest uppercase text-red-400/80 hover:text-red-300 flex items-center gap-1"
              >
                <Trash2 size={11} /> Reset
              </button>
            </div>
          </div>
          {halloweenAnalytics ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="border border-orange-900/50 rounded-lg p-3 bg-black/40 text-center" data-testid="hall-stat-views">
                  <div className="flex items-center justify-center gap-1 text-orange-400/80"><Eye size={12} /></div>
                  <p className="font-heading text-2xl text-white mt-1">{halloweenAnalytics.totals?.banner_view || 0}</p>
                  <p className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mt-0.5">Banner Views</p>
                </div>
                <div className="border border-orange-900/50 rounded-lg p-3 bg-black/40 text-center" data-testid="hall-stat-clicks">
                  <div className="flex items-center justify-center gap-1 text-orange-400/80"><MousePointerClick size={12} /></div>
                  <p className="font-heading text-2xl text-white mt-1">{halloweenAnalytics.totals?.cta_click || 0}</p>
                  <p className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mt-0.5">"Book The Fright" Clicks</p>
                  <p className="font-heading text-[10px] tracking-widest uppercase text-orange-400 mt-1">{halloweenAnalytics.cta_conversion_rate || 0}% CTR</p>
                </div>
                <div className="border border-orange-900/50 rounded-lg p-3 bg-black/40 text-center" data-testid="hall-stat-dismiss">
                  <div className="flex items-center justify-center gap-1 text-red-400/80"><XCircle size={12} /></div>
                  <p className="font-heading text-2xl text-white mt-1">{halloweenAnalytics.totals?.banner_dismiss || 0}</p>
                  <p className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mt-0.5">Dismisses</p>
                  <p className="font-heading text-[10px] tracking-widest uppercase text-red-400 mt-1">{halloweenAnalytics.dismiss_rate || 0}% dismiss</p>
                </div>
                <div className="border border-orange-900/50 rounded-lg p-3 bg-black/40 text-center" data-testid="hall-stat-scares">
                  <div className="flex items-center justify-center gap-1 text-orange-400/80"><Ghost size={12} /></div>
                  <p className="font-heading text-2xl text-white mt-1">{halloweenAnalytics.totals?.scare_fired || 0}</p>
                  <p className="font-heading text-[9px] tracking-widest uppercase text-neutral-500 mt-0.5">Jump Scares Fired</p>
                </div>
              </div>
              {Array.isArray(halloweenAnalytics.trend) && halloweenAnalytics.trend.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="hall-analytics-trend">
                    <thead>
                      <tr className="text-neutral-500 border-b border-neutral-800">
                        <th className="text-left py-1.5 pr-3 font-heading text-[10px] tracking-widest uppercase">Date</th>
                        <th className="text-right py-1.5 px-3 font-heading text-[10px] tracking-widest uppercase">Views</th>
                        <th className="text-right py-1.5 px-3 font-heading text-[10px] tracking-widest uppercase">Clicks</th>
                        <th className="text-right py-1.5 px-3 font-heading text-[10px] tracking-widest uppercase">Dismiss</th>
                        <th className="text-right py-1.5 pl-3 font-heading text-[10px] tracking-widest uppercase">Scares</th>
                      </tr>
                    </thead>
                    <tbody>
                      {halloweenAnalytics.trend.map(row => (
                        <tr key={row.date} className="border-b border-neutral-900/60 text-neutral-300">
                          <td className="py-1.5 pr-3 font-mono text-[11px]">{row.date}</td>
                          <td className="py-1.5 px-3 text-right">{row.banner_view || 0}</td>
                          <td className="py-1.5 px-3 text-right text-orange-300">{row.cta_click || 0}</td>
                          <td className="py-1.5 px-3 text-right text-red-300">{row.banner_dismiss || 0}</td>
                          <td className="py-1.5 pl-3 text-right">{row.scare_fired || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <p className="font-sans text-xs text-neutral-500 italic">No data yet — turn on the banner and share the page.</p>
          )}
        </div>
      </div>
    </div>
  );
}
